import React, { useState } from 'react'
import { StyleSheet, Text, View } from 'react-native'

import Button from '../../../UIControls/Button'
import styles, { colors } from '../../../styles/global'
import { resolveAssistantTemplateConflicts } from '../../../../utils/backends/Assistants/assistantsFirestore'
import { translate } from '../../../../i18n/TranslationService'
import { getAssistantReasoningEffortLabelKey } from '../../../../functions/Assistant/selectableAssistantReasoningEfforts'
import { getAssistantTemplateReviewLabelKey } from './templateReview'
import { formatTemplateConflictField } from './templateConflictFields'

// Re-exported so the existing importers (and UpdateFromTemplate.test.js) keep
// their entry point after the formatter moved to a Firestore-free module.
export { formatTemplateConflictField }

const MODEL_LABELS = {
    MODEL_GPT6_SOL: 'GPT 6.1 Sol',
    MODEL_GPT5_6_TERRA: 'GPT 5.6 Terra',
    MODEL_GPT6_LUNA: 'GPT 6 Luna',
    MODEL_DEEPSEEK_V4_FLASH: 'DeepSeek V4 Flash',
}

export const formatTemplateConflictValue = (field, value, exists) => {
    if (!exists) return translate('(removed)')
    if (field === 'model' || field === 'emailModel' || field === 'heartbeatModel') return MODEL_LABELS[value] || value
    if (field === 'reasoningEffort' || field === 'heartbeatReasoningEffort')
        return translate(getAssistantReasoningEffortLabelKey(value))
    if (typeof value === 'string') return value || translate('(empty)')
    try {
        return JSON.stringify(value, null, 2)
    } catch (error) {
        return String(value)
    }
}

export default function UpdateFromTemplate({ projectId, assistant, disabled }) {
    const [updatingField, setUpdatingField] = useState('')
    if (!assistant?.copiedFromTemplateAssistantId) return null

    const conflicts = Array.isArray(assistant.templateSyncConflicts) ? assistant.templateSyncConflicts : []
    const resolve = async (field, acceptTemplate) => {
        setUpdatingField(field)
        try {
            await resolveAssistantTemplateConflicts(projectId, assistant.uid, acceptTemplate ? [field] : [], [field])
        } finally {
            setUpdatingField('')
        }
    }
    const syncedLabel = assistant.templateSyncedAt
        ? `${translate('Synced from template')} • ${new Date(assistant.templateSyncedAt).toLocaleString()}`
        : translate('Linked to template • waiting for the next template update')

    return (
        <View style={[localStyles.container, conflicts.length ? localStyles.needsReview : null]}>
            <View style={localStyles.statusRow}>
                <View style={localStyles.statusCopy}>
                    {!!conflicts.length && (
                        <Text style={localStyles.reviewCount}>
                            {conflicts.length} {translate(getAssistantTemplateReviewLabelKey(conflicts.length))}
                        </Text>
                    )}
                    <Text style={localStyles.status}>{syncedLabel}</Text>
                </View>
            </View>
            {conflicts.map(conflict => (
                <View key={conflict.field} style={localStyles.conflict}>
                    <Text style={localStyles.field}>{formatTemplateConflictField(conflict.field)}</Text>
                    <Text style={localStyles.conflictHelp}>
                        {translate('Choose which version to use for this setting.')}
                    </Text>
                    <View style={localStyles.diffRow}>
                        <View style={[localStyles.valueColumn, localStyles.localColumn]}>
                            <Text style={localStyles.columnTitle}>{translate('Your version')}</Text>
                            <Text selectable style={localStyles.value}>
                                {formatTemplateConflictValue(
                                    conflict.field,
                                    conflict.localValue,
                                    conflict.localValueExists
                                )}
                            </Text>
                        </View>
                        <View style={[localStyles.valueColumn, localStyles.templateColumn]}>
                            <Text style={localStyles.columnTitle}>{translate('Template version')}</Text>
                            <Text selectable style={localStyles.value}>
                                {formatTemplateConflictValue(
                                    conflict.field,
                                    conflict.templateValue,
                                    conflict.templateValueExists
                                )}
                            </Text>
                        </View>
                    </View>
                    <View style={localStyles.actions}>
                        <View style={localStyles.actionButton}>
                            <Button
                                type="ghost"
                                title={translate('Keep mine')}
                                disabled={disabled || !!updatingField}
                                onPress={() => resolve(conflict.field, false)}
                            />
                        </View>
                        <View style={localStyles.actionButton}>
                            <Button
                                type="primary"
                                title={translate('Accept template')}
                                disabled={disabled || !!updatingField}
                                onPress={() => resolve(conflict.field, true)}
                            />
                        </View>
                    </View>
                </View>
            ))}
        </View>
    )
}

const localStyles = StyleSheet.create({
    container: {
        backgroundColor: colors.Grey100,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: colors.Grey300,
        padding: 16,
        marginBottom: 20,
    },
    needsReview: {
        backgroundColor: colors.UtilityYellow100,
        borderColor: colors.UtilityYellow150,
    },
    statusRow: { flexDirection: 'row', alignItems: 'center' },
    statusCopy: { flex: 1, minWidth: 220 },
    status: { ...styles.caption1, color: colors.Text02, marginTop: 2 },
    reviewCount: { ...styles.subtitle2, color: colors.UtilityYellow300 },
    conflict: {
        backgroundColor: '#ffffff',
        borderRadius: 8,
        borderWidth: 1,
        borderColor: colors.Grey300,
        marginTop: 16,
        padding: 16,
    },
    field: { ...styles.subtitle1, color: colors.Text01 },
    conflictHelp: { ...styles.caption1, color: colors.Text02, marginTop: 4 },
    diffRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        marginHorizontal: -4,
        marginTop: 12,
    },
    valueColumn: {
        flexGrow: 1,
        flexBasis: 260,
        minWidth: 220,
        minHeight: 88,
        borderRadius: 6,
        borderWidth: 1,
        borderLeftWidth: 4,
        padding: 12,
        margin: 4,
    },
    localColumn: {
        backgroundColor: colors.Grey100,
        borderColor: colors.Grey300,
        borderLeftColor: colors.Secondary100,
    },
    templateColumn: {
        backgroundColor: colors.UtilityGreen100,
        borderColor: colors.UtilityGreen125,
        borderLeftColor: colors.UtilityGreen300,
    },
    columnTitle: { ...styles.caption1, color: colors.Text02, marginBottom: 8 },
    value: { ...styles.body1, color: colors.Text01, flexShrink: 1 },
    actions: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'flex-end',
        marginHorizontal: -4,
        marginTop: 8,
    },
    actionButton: { marginHorizontal: 4, marginTop: 8 },
})
